import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Search, Calendar, Clock, Building2, FileText, Link2,
  Loader2, Trash2, ExternalLink, Filter, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMWorkLogs() {
  const { token, user, canViewPricing } = useCRMAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [formData, setFormData] = useState({
    client_id: '',
    work_type_id: '',
    quantity: 1,
    work_date: new Date().toISOString().split('T')[0],
    proof_url: '',
    work_link: '',
    notes: ''
  });

  useEffect(() => {
    fetchLogs();
    fetchClients();
    fetchWorkTypes();
    fetchDailySummary();
  }, [dateFilter]);

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API}/api/crm/tasks/logs/?date=${dateFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySummary = async () => {
    try {
      const res = await fetch(`${API}/api/crm/tasks/production/daily?date=${dateFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDailySummary(data);
      }
    } catch (err) {
      // Silent fail - data will show empty state
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/crm/clients/?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients);
      }
    } catch (err) {
      // Silent fail - data will show empty state
    }
  };

  const fetchWorkTypes = async () => {
    try {
      const res = await fetch(`${API}/api/crm/work-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkTypes(data.work_types);
      }
    } catch (err) {
      // Silent fail - data will show empty state
    }
  };

  const handleSaveLog = async () => {
    if (!formData.client_id || !formData.work_type_id) {
      toast.error('Please select client and work type');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/tasks/logs/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Work logged successfully!');
        setShowAddModal(false);
        setFormData({
          client_id: '',
          work_type_id: '',
          quantity: 1,
          work_date: new Date().toISOString().split('T')[0],
          proof_url: '',
          work_link: '',
          notes: ''
        });
        fetchLogs();
        fetchDailySummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to log work');
      }
    } catch (err) {
      toast.error('Failed to log work');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!confirm('Delete this work log?')) return;

    try {
      const res = await fetch(`${API}/api/crm/tasks/logs/${logId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Work log deleted');
        fetchLogs();
        fetchDailySummary();
      }
    } catch (err) {
      toast.error('Failed to delete log');
    }
  };

  // Group work types by category
  const groupedWorkTypes = workTypes.reduce((acc, wt) => {
    const cat = wt.category_name || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(wt);
    return acc;
  }, {});

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Work Logs</h1>
          <p className="text-slate-500">Track daily production and deliverables</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> Log Work
        </Button>
      </div>

      {/* Date Filter & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 col-span-1">
          <label className="text-sm font-medium text-slate-700 mb-2 block">Select Date</label>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full"
          />
        </Card>

        {dailySummary && (
          <>
            <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <p className="text-sm opacity-90">Total Items</p>
              <p className="text-3xl font-bold">{dailySummary.totals?.total_items || 0}</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <p className="text-sm opacity-90">Work Entries</p>
              <p className="text-3xl font-bold">{dailySummary.totals?.total_logs || 0}</p>
            </Card>
            {canViewPricing() && (
              <Card className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <p className="text-sm opacity-90">Total Value</p>
                <p className="text-3xl font-bold">₹{(dailySummary.totals?.total_value || 0).toLocaleString()}</p>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Work Type Summary */}
      {dailySummary?.summary?.length > 0 && (
        <Card className="p-4 mb-6">
          <h3 className="font-semibold text-slate-900 mb-3">Production by Type</h3>
          <div className="flex flex-wrap gap-2">
            {dailySummary.summary.map((item, idx) => (
              <Badge key={idx} variant="outline" className="py-1 px-3">
                {item.work_type_name}: <span className="font-bold ml-1">{item.total_quantity}</span>
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Work Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="p-12 text-center">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No work logged for this date</h3>
          <p className="text-slate-500 mb-4">Start tracking your work by adding an entry</p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Log Work
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Client</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Work Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Quantity</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">By</th>
                  {canViewPricing() && (
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Value</th>
                  )}
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Links</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-indigo-600" />
                        </div>
                        <span className="font-medium text-slate-900">{log.client_name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-slate-900">{log.work_type_name}</p>
                        <p className="text-xs text-slate-500">{log.category_name}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className="bg-blue-100 text-blue-800">{log.quantity}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{log.user_name}</td>
                    {canViewPricing() && (
                      <td className="py-3 px-4 font-medium text-emerald-600">
                        ₹{(log.total_value || 0).toLocaleString()}
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        {log.work_link && (
                          <a href={log.work_link} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                        {log.proof_url && (
                          <a href={log.proof_url} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <FileText className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleDeleteLog(log.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add Work Log Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Completed Work</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Client *</label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Work Type *</label>
              <Select value={formData.work_type_id} onValueChange={(v) => setFormData({...formData, work_type_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select work type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groupedWorkTypes).map(([category, types]) => (
                    <div key={category}>
                      <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">{category}</div>
                      {types.map(wt => (
                        <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Work Date</label>
                <Input
                  type="date"
                  value={formData.work_date}
                  onChange={(e) => setFormData({...formData, work_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Work Link (optional)</label>
              <Input
                placeholder="https://instagram.com/p/..."
                value={formData.work_link}
                onChange={(e) => setFormData({...formData, work_link: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Proof URL (optional)</label>
              <Input
                placeholder="Link to proof/screenshot"
                value={formData.proof_url}
                onChange={(e) => setFormData({...formData, proof_url: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <Textarea
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleSaveLog} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Log Work'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
